import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material'
import React, { useState } from 'react'
import { test_questions } from './test_questions'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { traits_avatar } from './traits_avatar'
import SubmissionModal from '../SubmissionModal'
import { useMutation, useQueryClient } from 'react-query'
import {
  retakeAssessment,
  submitAssessment,
} from 'services/api/modules/performance/personality-test-assessment/assessment'
import { useNavigate } from 'react-router-dom'
import AdxButton from 'components/custom/AdxButton'

const QuestionsPage = ({ setProgress, isRetake }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState({ factor: 0, number: 0 })
  const [answers, setAnswers] = useState(
    test_questions.map((item, index) => ({
      factor: index + 1,
      scores: item.questions.map(() => null),
    }))
  )

  const { mutate: submitTest, isLoading: submitLoading } = useMutation(submitAssessment, {
    onSuccess: async () => {
      console.log('Submitted successfully!')
      navigate('/app/performance/assessment')
      queryClient.invalidateQueries('assessmentResult')
    },
    onError: async () => {
      navigate('/app/performance/assessment')
    },
  })

  const { mutate: retakeTest, isLoading: retakeLoading } = useMutation(retakeAssessment, {
    onSuccess: async () => {
      console.log('Submitted successfully!')
      navigate('/app/performance/assessment')
      queryClient.invalidateQueries('assessmentResult')
    },
    onError: async () => {
      navigate('/app/performance/assessment')
    },
  })

  const currentPage = test_questions[page.factor].questions[page.number]
  const currentAnswer = answers[page.factor].scores[page.number]
  const lastPage = {
    factor: test_questions.length - 1,
    number: test_questions[test_questions.length - 1].questions.length - 1,
  }

  const prevPage = () => {
    setPage((prevState) => {
      if (prevState.number === 0) {
        if (prevState.factor === 0) {
          return prevState
        } else {
          const prevFactor = prevState.factor - 1
          const lastQuestion = test_questions[prevFactor].questions.length - 1
          return { factor: prevFactor, number: lastQuestion }
        }
      } else {
        return { ...prevState, number: prevState.number - 1 }
      }
    })
  }

  const progress = () => {
    const totalLength = answers.reduce((total, item) => total + item.scores.length, 0)
    const totalNullCount = answers
      .flatMap((item) => item.scores)
      .filter((value) => value === null).length
    const percentageDone = ((totalLength - totalNullCount) / totalLength) * 100

    return percentageDone
  }

  const nextPage = () => {
    setPage((prevState) => {
      const nextPage = prevState.number + 1
      if (nextPage === test_questions[prevState.factor].questions.length) {
        return { factor: prevState.factor + 1, number: 0 }
      } else {
        return { ...prevState, number: nextPage }
      }
    })
  }

  const handleAnswer = (e) => {
    const updatedAnswers = [...answers]
    updatedAnswers[page.factor].scores[page.number] = JSON.parse(e.target.value)
    setAnswers(updatedAnswers)

    if (!(page.factor === lastPage.factor && page.number === lastPage.number)) {
      setTimeout(() => {
        nextPage()
      }, 1500) // Delay in milliseconds (e.g., 1000ms = 1 second)
    }

    setProgress(progress())
  }

  const checkTraits = (score) => {
    if (score < 50) {
      return 0
    } else if (score === 50) {
      return 1
    } else {
      return 2
    }
  }

  const calculateResult = (scores, index) => {
    const maxScore = test_questions[index].max
    const totalScore = scores.reduce((accumulator, currentValue) => accumulator + currentValue, 0)
    const finalScore = Math.round((totalScore / maxScore) * 50 + 50) / 100
    const resultTraits = traits_avatar[index].traits[checkTraits(finalScore)]

    return { factor: resultTraits, score: finalScore }
  }

  const handleSubmit = () => {
    const calculatedResult = answers.map((answer, index) => calculateResult(answer.scores, index))

    const body = {
      ambitious_score: calculatedResult[4].score,
      freedom_score: calculatedResult[0].score,
      adventurous_score: calculatedResult[1].score,
      teamwork_score: calculatedResult[2].score,
      systematic_score: calculatedResult[3].score,
    }

    if (isRetake) {
      retakeTest(body)
    } else {
      submitTest(body)
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: 'white.main',
        width: '600px',
        minHeight: 'calc(100vh - 62px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          padding: '32px 30px 40px',
        }}
      >
        <Typography variant="h4" mb={'30px'}>
          {currentPage.question}
        </Typography>
        <FormControl sx={{ width: '100%' }}>
          <RadioGroup value={currentAnswer} onChange={handleAnswer}>
            {currentPage.options.map((item, index) => (
              <FormControlLabel
                key={index}
                className="text-adx-darkerGray"
                value={item.value}
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: '#E5E5E5',
                      '&.MuiRadio-root': {
                        marginRight: '14px',
                        padding: 0,
                      },
                    }}
                    checkedIcon={<CheckCircleRoundedIcon sx={{ color: 'alternate.main' }} />}
                  />
                }
                label={item.option}
                sx={{
                  '&.MuiFormControlLabel-root': {
                    borderRadius: '4.5px',
                    backgroundColor: currentAnswer === item.value ? '#E2F9F8' : '',
                    marginBottom: '10px',
                    marginX: 0,
                    padding: '14px',
                  },
                  '.MuiFormControlLabel-label': {
                    fontSize: '14px',
                    color: currentAnswer === item.value ? '#1B8379' : '',
                  },
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          padding: '24px 30px',
          borderTop: '1px solid #E5E5E5',
        }}
      >
        <AdxButton
          variant={'outlined'}
          onClick={prevPage}
          disabled={page.factor === 0 && page.number === 0}
          size={'small'}
          sx={{ width: '150px' }}
        >
          Previous
        </AdxButton>
        {page.factor === lastPage.factor && page.number === lastPage.number ? (
          <SubmissionModal
            handleSubmit={handleSubmit}
            disabled={currentAnswer === null}
            isLoading={submitLoading || retakeLoading}
          />
        ) : (
          <AdxButton
            variant={'contained'}
            onClick={nextPage}
            disabled={currentAnswer === null}
            size={'small'}
            sx={{ width: '150px' }}
          >
            Next
          </AdxButton>
        )}
      </Box>
    </Box>
  )
}

export default QuestionsPage
